import { css, useTheme } from "@emotion/react";
import { useTranslation } from "react-i18next";

import DefaultButton from "./Button.jsx";
import handleComponentsProp from "./handleComponentsProp";
import Icon from "./Icon.jsx";
import useThemeProps from "./useThemeProps.jsx";

function Label({ as: Component = "span", visuallyHidden, ...props }) {
  return (
    <Component
      {...props}
      css={css`
        ${visuallyHidden &&
        css`
          position: absolute;
          clip: rect(0, 0, 0, 0);
          pointer-events: none;
        `}
      `}
    />
  );
}

function List({ as: Component = "ul", ...props }) {
  return (
    <Component
      {...props}
      css={css`
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        margin-top: 0;
        margin-bottom: 0;
        padding: 0;
        list-style: none;
      `}
    />
  );
}

function ListItem({ as: Component = "li", ...props }) {
  return <Component {...props} css={css``} />;
}

export default function Pagination(props) {
  // eslint-disable-next-line no-unused-vars
  const theme = useTheme();
  props = useThemeProps({ props, name: "Pagination" });
  let {
    page,
    onChange,
    siblingCount = 2,
    count,
    showFirstButton = false,
    showLastButton = false,
    hidePrevButton = false,
    hideNextButton = false,
    showLabels = false,
    align = "justify",
    buttonVariant = "pagination",
    components,
  } = props;
  const { t } = useTranslation();

  let { Button } = handleComponentsProp(components, { Button: DefaultButton });

  const isFirstPage = page === 0;
  const isLastPage = page === count - 1;
  let pagesToDisplay;
  let range = siblingCount * 2 + 1;

  if (count > range) {
    let min = Math.min(
      count - range,
      Math.max(0, page - Math.floor(range / 2)),
    );
    pagesToDisplay = Array.from({ length: range }, (v, i) => min + i);
  } else {
    pagesToDisplay = Array.from({ length: count }, (v, i) => i);
  }

  return (
    <List
      css={css`
        justify-content: ${align === "justify" ? "space-between" : align};
      `}
    >
      {showFirstButton && (
        <ListItem>
          <Button
            disabled={isFirstPage}
            onClick={(event) => {
              if (!isFirstPage) {
                onChange(event, 0);
              }
            }}
            variant={buttonVariant}
          >
            <Icon name="arrow-previous" />
            <Label visuallyHidden={!showLabels}>
              {t(["wsui.base.pagination.firstPage", "First page"])}
            </Label>
          </Button>
        </ListItem>
      )}
      {!hidePrevButton && (
        <ListItem>
          <Button
            disabled={isFirstPage}
            onClick={(event) => {
              if (!isFirstPage) {
                onChange(event, page - 1);
              }
            }}
            variant={buttonVariant}
          >
            <Icon name="chevron-left" />
            <Label visuallyHidden={!showLabels}>
              {t(["wsui.base.pagination.prevPage", "Previous page"])}
            </Label>
          </Button>
        </ListItem>
      )}
      {pagesToDisplay.map((pageIndex, index) => {
        return (
          <ListItem
            key={pageIndex}
            css={css`
              margin-inline-start: ${index === 0 && align === "justify"
                ? "auto"
                : null};
              margin-inline-end: ${index === pagesToDisplay.length - 1 &&
              align === "justify"
                ? "auto"
                : null};
            `}
          >
            <Button
              aria-current={pageIndex == page ? "page" : null}
              onClick={(event) => {
                onChange(event, pageIndex);
              }}
              variant={buttonVariant}
            >
              {pageIndex + 1}
            </Button>
          </ListItem>
        );
      })}
      {!hideNextButton && (
        <ListItem>
          <Button
            disabled={isLastPage}
            onClick={(event) => {
              if (!isLastPage) {
                onChange(event, page + 1);
              }
            }}
            variant={buttonVariant}
          >
            <Label visuallyHidden={!showLabels}>
              {t(["wsui.base.pagination.nextPage", "Next page"])}
            </Label>
            <Icon name="chevron-right" />
          </Button>
        </ListItem>
      )}
      {showLastButton && (
        <ListItem>
          <Button
            disabled={isLastPage}
            onClick={(event) => {
              if (!isLastPage) {
                onChange(event, count - 1);
              }
            }}
            variant={buttonVariant}
          >
            <Label visuallyHidden={!showLabels}>
              {t(["wsui.base.pagination.lastPage", "Last page"])}
            </Label>
            <Icon name="arrow-next" />
          </Button>
        </ListItem>
      )}
    </List>
  );
}
